import React, { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { AppointmentService } from "../../services/api/appointment.service";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";
import { formatAge } from "../../shared/AddNewPatientModal";

interface Patient {
  name: string;
  phone: string;
  age: number;
  gender: string;
  type: string;
}

interface Doctor {
  role: string;
  name: string;
  qualification: string;
  phone: string;
}

interface Appointment {
  id: string;
  time: string;
  startTime: string;
  patient: Patient;
  doctor: Doctor;
}

interface AppointmentRowProps {
  appointment: Appointment;
  onDelete: (id: string) => void;
}

const AppointmentRow: React.FC<AppointmentRowProps> = ({
  appointment,
  onDelete,
}) => (
  <div className="flex items-center p-4 hover:bg-gray-50 border-b border-gray-100">
    {/* Time Column */}
    <div className="w-24">
      <div className="text-sm font-medium text-gray-900">{appointment.time}</div>
    </div>

    {/* Patient Details */}
    <div className="flex-1 ml-4">
      <div className="flex items-center">
        <div className="h-2 w-2 rounded-full bg-green-500 mr-3"></div>
        <div>
          <div className="text-sm font-medium text-gray-900">
            {appointment.patient.name}
          </div>
          <div className="flex items-center text-xs text-gray-500 mt-1 space-x-4">
            <span>+{appointment.patient.phone}</span>
            <span>Age: {formatAge(appointment.patient.age)}</span>
            <span>{appointment.patient.gender}</span>
            {/* <span className="px-2 py-1 bg-gray-100 rounded">
              {patient.type}
            </span> */}
          </div>
        </div>
      </div>
    </div>

    {/* Doctor Details */}
    <div className="flex-1 ml-4">
      <div className="text-sm font-medium">
        <div className="text-gray-600">{appointment.doctor.role}</div>
        <div className="flex items-center text-xs text-gray-500 mt-1 space-x-4">
          <span>{appointment.doctor.name}</span>
          <span>{appointment.doctor.qualification}</span>
          <span>+{appointment.doctor.phone}</span>
        </div>
      </div>
    </div>

    {/* Actions */}
    <div className="flex items-center space-x-2 ml-4">
      {/* <button
        className="p-1 hover:bg-gray-100 rounded"
        onClick={() => console.log("Edit appointment")}
      >
        <Edit2 className="h-4 w-4 text-gray-500" />
      </button> */}
      <button
        className="p-1 hover:bg-gray-100 rounded"
        onClick={() => {
          console.log("Deleting Appointment ID:", appointment.id);
          onDelete(appointment.id);
        }}
      >
        <Trash2 className="h-4 w-4 text-gray-500" />
      </button>
    </div>
  </div>
);

const AppointmentsList: React.FC = () => {
  const { user } = useAuth();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchAppointments = async () => {
      try {
        const today = new Date().toISOString().split("T")[0];
        const clinicId = user?.id;
        const response = await AppointmentService.getAppointmentListing(
          today,
          "Booked",
          clinicId
        );

        if (response.success && response.data) {
          const sortedAppointments: Appointment[] = response.data
            .map((item: any) => ({
              id:item.id,
              time: `${item.appointment_start_time} - ${item.appointment_end_time}`,
              startTime: item.appointment_start_time, // Store start time for sorting
              patient: {
                name: item.patientDetails.name,
                phone: item.patientDetails.mobileNumber,
                age: item.patientDetails.age,
                gender: item.patientDetails.gender,
                type: item.consultation_type,
              },
              doctor: {
                role: "Consulting Doctor",
                name: item.doctorDetails.name,
                qualification: item.doctorDetails.education.join(", "),
                phone: item.doctorDetails.mobileNumber,
              },
            }))
            .sort((a, b) => {
              // Convert time (HH:mm) to minutes and compare
              const timeToMinutes = (time: string) => {
                const [hours, minutes] = time.split(":").map(Number);
                return hours * 60 + minutes;
              };
              return timeToMinutes(a.startTime) - timeToMinutes(b.startTime);
            });

          setAppointments(sortedAppointments);
        }
      } catch (error) {
        console.error("Failed to fetch appointments:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAppointments();
  }, []);

  const handleDelete = async (appointmentId: string) => {
    if (!window.confirm("Are you sure you want to cancel this appointment?")) {
      return;
    }

    try {
      const response = await AppointmentService.cancleAppointment(
        appointmentId
      );
      if (response.success) {
        setAppointments((prev) =>
          prev.filter((appointment) => appointment.id !== appointmentId)
        );
        toast.success("Appointment cancelled sucessfully")
      } else {
        toast.error("Failed to cancel appointment: " + response.message);
      }
    } catch (error) {
      console.error("Error canceling appointment:", error);
    }
  };

  return (
    <div className="w-full mx-auto bg-white rounded-lg shadow">
      <div className="p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">
          Upcoming Appointments
        </h2>
        {loading ? (
          <p className="text-gray-600">Loading appointments...</p>
        ) : appointments.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {appointments.map((appointment) => (
              <AppointmentRow
                key={appointment.id}
                appointment={appointment}
                onDelete={handleDelete}
              />
            ))}
          </div>
        ) : (
          <p className="text-gray-600">No booked appointments found.</p>
        )}
      </div>
    </div>
  );
};

export default AppointmentsList;